import React, { createContext, useContext } from "react";
import { checkIsArrayEmpty } from "repoV2/utils/common/dataTypes/array";
import { getDummyImageSrc } from "repoV2/utils/common/render/images";
import {
    DEFAULT_IMG_TESTIMONIAL_PLACEHOLDER,
    DEFAULT_PROFILE_PIC_SRC,
} from "repoV2/utils/common/assets/assets";
import { useSalePageContext } from "../SalePageContext";
import {
    TESTIMONIAL_TYPE,
    DEFAULT_SOURCES,
    ADD_VIDEO_OPTIONS,
    TESTIMONIAL_DISPLAY_TYPE,
} from "../constants";

type IReviewsSectionContextValue = {
    hide: boolean;
    title: string;
    reviewsList: Array<{
        reviewerName: string;
        reviewerImgSrc: string;
        reviewContent: string;
        testimonialType: string;
        testimonialImgSrc: string;
        testimonialVideoSrc: string;
        testimonialVideoSrcType: string;
    }>;
    listDesign:
        | typeof TESTIMONIAL_DISPLAY_TYPE.LIST
        | typeof TESTIMONIAL_DISPLAY_TYPE.CAROUSEL;
};

const initialContextValue: IReviewsSectionContextValue = {
    hide: false,
    title: "",
    reviewsList: [],
    listDesign: TESTIMONIAL_DISPLAY_TYPE.LIST,
};

const dummyData: IReviewsSectionContextValue["reviewsList"] = Array.from(
    Array(3)
).map((x: any, i: number) => {
    const idx = i + 1;
    return {
        reviewerName: `&lt;reviewer name ${idx}&gt;`,
        reviewerImgSrc: getDummyImageSrc({
            width: 200,
            height: 200,
            text: `reviewer ${idx}`,
        }),
        reviewContent: `&lt;review content ${idx}&gt;`,
        testimonialType: TESTIMONIAL_TYPE.TEXT,
        testimonialImgSrc: getDummyImageSrc({
            width: 200,
            height: 200,
            text: `reviewer ${idx} image`,
        }),
        testimonialVideoSrc: DEFAULT_SOURCES.VIDEO,
        testimonialVideoSrcType: ADD_VIDEO_OPTIONS.ADD_LINK.slug,
    };
});

const ReviewsSectionContext = createContext(initialContextValue);
export function ReviewsSectionContextProvider({
    children,
}: {
    children: React.ReactNode;
}) {
    const { sectionsData, isPreview } = useSalePageContext();
    const title =
        sectionsData.testimonialsSection.title ||
        (isPreview ? "&lt;Testimonials title&gt;" : "");
    const unprocessedReviewsList =
        (isPreview &&
        checkIsArrayEmpty(sectionsData.testimonialsSection.testimonialsList)
            ? dummyData
            : sectionsData.testimonialsSection.testimonialsList) || [];
    const reviewsList = unprocessedReviewsList.map(i => ({
        ...i,
        reviewerImgSrc: i.reviewerImgSrc || DEFAULT_PROFILE_PIC_SRC,
        testimonialType: i.testimonialType || TESTIMONIAL_TYPE.TEXT,
        testimonialImgSrc:
            i.testimonialImgSrc || DEFAULT_IMG_TESTIMONIAL_PLACEHOLDER,
        testimonialVideoSrc: i.testimonialVideoSrc || DEFAULT_SOURCES.VIDEO,
        reviewerImgSrcUrl: i.reviewerImgSrc,
    }));

    const listDesign =
        sectionsData.testimonialsSection.listDesign ===
        TESTIMONIAL_DISPLAY_TYPE.CAROUSEL
            ? TESTIMONIAL_DISPLAY_TYPE.CAROUSEL
            : TESTIMONIAL_DISPLAY_TYPE.LIST;
    const contextValue: IReviewsSectionContextValue = {
        hide: checkIsArrayEmpty(reviewsList),
        title,
        reviewsList,
        listDesign,
    };
    return (
        <ReviewsSectionContext.Provider value={contextValue}>
            {children}
        </ReviewsSectionContext.Provider>
    );
}

export function useReviewsSectionContext() {
    return useContext(ReviewsSectionContext);
}
